import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  formatPct,
  formatScoreValue,
  deltaIsImprovement,
  scoreToneColor,
  metricHeading,
  isFiniteNumber,
} from './scoreUtils';

// ScoreCard renders one AnalysisScore block (the canonical
// {metric, before, after_if_applied, delta, delta_pct, …} shape
// returned by every tree-optimisation analyser). It supports the
// two "shapes" the backend emits:
//   * actionable  — has both `before` and `after_if_applied`; we
//                   show before → after with a delta chip.
//   * advisory    — `is_advisory: true`; we show a single 0–100
//                   reading with an interpretive sentence.
//
// `compact` collapses the layout for sub-tab rows; `dense` removes
// the surrounding Paper so the card can be embedded inside another
// container.
const ScoreCard = ({ score, title, dense = false, compact = false }) => {
  if (!score) {
    return null;
  }

  const heading = title || metricHeading(score);
  const tone = scoreToneColor(score);
  const improved = deltaIsImprovement(score);

  const beforeText = formatScoreValue(score.before, score.unit);
  const afterText = isFiniteNumber(score.after_if_applied)
    ? formatScoreValue(score.after_if_applied, score.unit)
    : null;
  const deltaPctText = formatPct(score.delta_pct);

  let TrendIcon = TrendingFlatIcon;
  if (improved === true) TrendIcon = score.direction === 'higher_is_better' ? TrendingUpIcon : TrendingDownIcon;
  else if (improved === false) TrendIcon = score.direction === 'higher_is_better' ? TrendingDownIcon : TrendingUpIcon;

  // For advisory 0–100 metrics (Health, Backup) we render a small
  // progress bar so operators can eyeball it at a glance.
  const showAdvisoryBar = score.is_advisory
    && (score.unit === '0\u2013100' || score.unit === '0-100')
    && isFiniteNumber(score.before);

  const advisoryBarColor = (() => {
    if (!isFiniteNumber(score.before)) return 'inherit';
    if (score.before >= 80) return 'success';
    if (score.before >= 60) return 'info';
    if (score.before >= 40) return 'warning';
    return 'error';
  })();

  const body = (
    <Box>
      <Stack
        direction={compact ? 'row' : { xs: 'column', sm: 'row' }}
        spacing={compact ? 1 : 1.5}
        alignItems={compact ? 'center' : { xs: 'flex-start', sm: 'center' }}
        sx={{ mb: score.summary_text ? 1 : 0 }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
            <Typography variant={compact ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600 }}>
              {heading}
            </Typography>
            {score.metric_help && (
              <Tooltip title={score.metric_help} placement="top" arrow>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
            )}
            {score.is_advisory && (
              <Tooltip title="Advisory metric — there is no automatic fix; treat as a single-snapshot reading.">
                <Chip
                  label="Advisory"
                  size="small"
                  variant="outlined"
                  icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ height: 18, fontSize: 10 }}
                />
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
            <Typography variant={compact ? 'body2' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {beforeText}
            </Typography>
            {afterText && (
              <>
                <TrendIcon
                  fontSize="small"
                  color={tone === 'default' ? 'inherit' : tone}
                />
                <Typography variant={compact ? 'body2' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {afterText}
                </Typography>
              </>
            )}
            {score.unit && score.unit !== '0\u2013100' && score.unit !== '0-100' && (
              <Typography variant="caption" color="text.secondary">
                {score.unit}
              </Typography>
            )}
          </Stack>
        </Box>

        {deltaPctText && !score.is_advisory && (
          <Chip
            label={deltaPctText}
            size="small"
            color={tone === 'default' ? 'default' : tone}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {showAdvisoryBar && (
        <Box sx={{ mt: 0.5, mb: score.summary_text ? 1 : 0 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, score.before))}
            color={advisoryBarColor}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {score.summary_text && !compact && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {score.summary_text}
        </Typography>
      )}
    </Box>
  );

  if (dense) return body;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: compact ? 1 : 1.5,
        borderColor: tone === 'default' ? 'divider' : `${tone}.main`,
        borderLeftWidth: 4,
      }}
    >
      {body}
    </Paper>
  );
};

export default ScoreCard;
